// One column's filter as the spreadsheet offers it: a checklist of the values the column holds
// among the rows every other filter admits, `(Blank)` when a row has none, OK to commit.
//
// A popover hanging off the funnel, not a modal: it is the spreadsheet's header dropdown, and a
// centred dialog with a backdrop reads as a form to fill in rather than a list to tick. It is a
// native popover because the table scrolls inside a box whose overflow would clip anything
// positioned within it; the top layer is above that, and light dismiss and Escape come with it.
// Placed under its anchor and left-aligned, flipping to the anchor's right edge or above it when
// the viewport ends first, so a column at the table's right edge opens leftwards. Rendered into
// the body rather than where it is written: inside a header cell it would inherit the header's
// small caps and its buttons would be styled as sort controls.
//
// The popover stays mounted and `open` says whether it is shown, because an opener that names it
// as its target (the funnel) shows and hides it natively - that is what makes a click on the
// funnel a toggle rather than a dismiss and a reopen - and a target has to exist to be invoked.
// The checklist inside is mounted only while it is open, so each opening starts afresh: the list
// is read then, not ahead of time for every column, so it is the current data and nothing has to
// refresh it after a save. The server excludes this column's own filter from the narrowing - a
// ticked value must stay on offer to be unticked - so the whole filter set is sent as it is.
//
// OK commits once: a change to five ticks is one page read and one totals read, not five of
// each. Every offered value ticked is no filter on this column, which is what the URL records,
// and is how the list opens when the column has no filter in force.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { getDeliveryFacet } from '../api.js';

export const BLANK = '';
export const BLANK_LABEL = '(Blank)';

// The gap between the anchor and the popover, and the least the popover keeps from a viewport
// edge, in pixels.
const GAP = 4;
const MARGIN = 8;

// `search` is the whole filter set as the `f.*` query string; `initial` is this column's set, or
// undefined when it has none. `anchorRef` is the control the popover hangs off; `id` is for an
// opener that names the popover as its target. `onToggle` reports every show and hide, whoever
// caused it: the opener, Cancel, OK, Escape or a click outside.
export default function FilterPopover({
  id, open, leg, column, search, initial, anchorRef, onCommit, onToggle,
}) {
  const ref = useRef(null);
  const [place, setPlace] = useState(null);

  // Shown or hidden to match `open` when the two differ - they agree already when the opener
  // did it natively - and placed before paint, then again whenever its size changes, since the
  // list arriving is what decides whether it fits below the anchor.
  useLayoutEffect(() => {
    const popover = ref.current;
    const shown = popover.matches(':popover-open');
    if (open && !shown) popover.showPopover();
    if (!open && shown) popover.hidePopover();
    if (!open) return undefined;

    const placeUnderAnchor = () => {
      const anchor = anchorRef.current.getBoundingClientRect();
      let left = anchor.left;
      let top = anchor.bottom + GAP;
      if (left + popover.offsetWidth > window.innerWidth - MARGIN) {
        left = Math.max(MARGIN, anchor.right - popover.offsetWidth);
      }
      if (top + popover.offsetHeight > window.innerHeight - MARGIN) {
        top = Math.max(MARGIN, anchor.top - popover.offsetHeight - GAP);
      }
      setPlace({ top, left });
    };
    placeUnderAnchor();
    const observer = new ResizeObserver(placeUnderAnchor);
    observer.observe(popover);
    // The page scrolling under an open popover moves the anchor and not the popover, which is
    // fixed; scroll does not dismiss a popover, so it follows instead.
    window.addEventListener('scroll', placeUnderAnchor, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', placeUnderAnchor, true);
    };
  }, [open, anchorRef]);

  return createPortal(
    <div
      ref={ref}
      id={id}
      popover="auto"
      role="dialog"
      aria-label={`Filter ${column.label}`}
      className="filter-popover"
      style={place ?? undefined}
      onToggle={(event) => onToggle(event.newState === 'open')}
    >
      {open && (
        <Checklist leg={leg} column={column} search={search} initial={initial}
                   onCommit={onCommit} onCancel={() => onToggle(false)} />
      )}
    </div>,
    document.body,
  );
}

function Checklist({ leg, column, search, initial, onCommit, onCancel }) {
  const [offered, setOffered] = useState(null);
  const [error, setError] = useState(null);
  const [ticked, setTicked] = useState(() => new Set(initial ?? []));
  const unfiltered = initial === undefined;

  // The list is of the filter set it will be committed over. That set can change under an open
  // popover - the back button rewrites the URL - so it is read again for the set it then belongs
  // to. The flag rather than `initial` itself: the query is rebuilt from the URL on every render,
  // so the array is a new one each time and would read the list again for nothing.
  useEffect(() => {
    let cancelled = false;
    getDeliveryFacet(leg, column.key, search)
      .then((facet) => {
        if (cancelled) return;
        const list = [...(facet.blank ? [BLANK] : []), ...facet.values];
        setOffered(list);
        if (unfiltered) setTicked(new Set(list));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [leg, column.key, search, unfiltered]);

  const toggle = (value) => {
    setTicked((current) => {
      const next = new Set(current);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const submit = (event) => {
    event.preventDefault();
    const all = offered.every((value) => ticked.has(value));
    onCommit(column.key, all ? [] : offered.filter((value) => ticked.has(value)));
  };

  return (
    <form onSubmit={submit}>
      {error && <p className="error">{error}</p>}
      {!offered && !error && <p className="muted">Loading…</p>}
      {offered && (
        <>
          <div className="filter-popover-controls">
            <button type="button" className="link" onClick={() => setTicked(new Set(offered))}
                    autoFocus>
              Select all
            </button>
            <button type="button" className="link" onClick={() => setTicked(new Set())}>
              Clear
            </button>
          </div>
          <ul className="filter-popover-values">
            {offered.map((value) => (
              <li key={value}>
                <label>
                  <input type="checkbox" checked={ticked.has(value)}
                         onChange={() => toggle(value)} />
                  {value === BLANK ? <em>{BLANK_LABEL}</em> : value}
                </label>
              </li>
            ))}
          </ul>
        </>
      )}
      <menu>
        <button type="button" onClick={onCancel}>Cancel</button>
        <button type="submit" disabled={!offered}>OK</button>
      </menu>
    </form>
  );
}
